import React, { useEffect, useRef, useState, useCallback } from 'react';
import { resolveCssVar } from '../../lib/themeColor';

export interface CropInsets {
  top: number;    // percent (0-45) trimmed from each side of the ROTATED image
  right: number;
  bottom: number;
  left: number;
}

interface ImageEditPreviewProps {
  imageUrl: string;
  rotation: number;              // degrees, -180..180
  cropMode: boolean;             // show the draggable crop rectangle overlay
  cropInsets: CropInsets;
  onCropInsetsChange: (insets: CropInsets) => void;
  /**
   * Hex of a palette swatch currently under the pointer (BackgroundColorTool's
   * SwatchWithEditor hover) — while set, every drawn pixel NOT within
   * HIGHLIGHT_TOLERANCE of this color is dimmed, so the matching region reads
   * as visually "lit up" against the rest of the image. Per direct user
   * request ("On hover of the Color highlight the specific color in image
   * preview"). Null/undefined draws normally.
   */
  highlightHex?: string | null;
}

const PREVIEW_BOX = 240; // fits inside the 280px left tool panel with margin
const HANDLE_SIZE = 10;
const MIN_CROP_PX = 24; // smallest crop rect side, in canvas-preview pixels — prevents dragging a handle past its opposite one
const CHECKER_SIZE = 10; // px per checkerboard square, at PREVIEW_BOX's own resolution

// Standard image-editor transparency indicator (light/mid gray checkerboard)
// drawn behind the image on every redraw. Without this, a pixel this app's
// own Remove Background step just made transparent renders as whatever the
// preview container's own background color is — reported live as "after
// Remove Background it is not visible for the user," since the removed area
// blended into the panel's solid background instead of visibly reading as
// "empty."
function drawCheckerboard(ctx: CanvasRenderingContext2D, size: number) {
  for (let y = 0; y < size; y += CHECKER_SIZE) {
    for (let x = 0; x < size; x += CHECKER_SIZE) {
      const isDark = ((x / CHECKER_SIZE) + (y / CHECKER_SIZE)) % 2 === 0;
      ctx.fillStyle = isDark ? '#e2e8f0' : '#ffffff';
      ctx.fillRect(x, y, CHECKER_SIZE, CHECKER_SIZE);
    }
  }
}

type DragTarget =
  | { kind: 'move' }
  | { kind: 'handle'; edge: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' };

// Canvas-based preview used by every wizard step (not just crop) so the same
// component renders consistently start to finish — per direct user request,
// this replaces the earlier plain percentage-slider crop UI with an actual
// drag-to-resize rectangle over the image, since the sliders were confusing.
// Rotation is baked into the drawn image (not a CSS transform) so the crop
// rectangle's pixel coordinates always line up with what the user visually
// sees, exactly matching how BackgroundColorTool's applyCropRotate bakes the
// final result server-side (rotate first, then crop the rotated bounds).
export function ImageEditPreview({ imageUrl, rotation, cropMode, cropInsets, onCropInsetsChange, highlightHex }: ImageEditPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  // Layout of the rotated image as actually drawn inside the fixed
  // PREVIEW_BOX square — everything else (handle positions, drag math) is
  // expressed in these same canvas-pixel coordinates.
  const [rotatedLayout, setRotatedLayout] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const dragRef = useRef<{ target: DragTarget; startX: number; startY: number; startInsets: CropInsets } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setImgLoaded(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { if (!cancelled) { imgRef.current = img; setImgLoaded(true); } };
    img.src = imageUrl;
    return () => { cancelled = true; };
  }, [imageUrl]);

  // Redraw whenever anything visual changes — image, rotation, or (in crop
  // mode) the insets being dragged live.
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = PREVIEW_BOX;
    canvas.height = PREVIEW_BOX;
    ctx.clearRect(0, 0, PREVIEW_BOX, PREVIEW_BOX);
    drawCheckerboard(ctx, PREVIEW_BOX);

    const radians = (rotation * Math.PI) / 180;
    const srcW = img.naturalWidth, srcH = img.naturalHeight;
    const rotatedW = Math.abs(srcW * Math.cos(radians)) + Math.abs(srcH * Math.sin(radians));
    const rotatedH = Math.abs(srcW * Math.sin(radians)) + Math.abs(srcH * Math.cos(radians));

    // Fit the rotated bounding box inside the preview square, centered —
    // never upscale past the box.
    const scale = Math.min(PREVIEW_BOX / rotatedW, PREVIEW_BOX / rotatedH, 1);
    const drawnW = rotatedW * scale, drawnH = rotatedH * scale;
    const originX = (PREVIEW_BOX - drawnW) / 2, originY = (PREVIEW_BOX - drawnH) / 2;
    setRotatedLayout({ x: originX, y: originY, w: drawnW, h: drawnH });

    ctx.save();
    ctx.translate(PREVIEW_BOX / 2, PREVIEW_BOX / 2);
    ctx.rotate(radians);
    ctx.scale(scale, scale);
    ctx.drawImage(img, -srcW / 2, -srcH / 2, srcW, srcH);
    ctx.restore();

    if (highlightHex) dimNonMatchingPixels(ctx, originX, originY, drawnW, drawnH, highlightHex);

    if (cropMode) {
      const rect = insetsToRect(cropInsets, originX, originY, drawnW, drawnH);
      // Dim everything outside the crop rect, like a standard photo cropper.
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillRect(originX, originY, drawnW, rect.y - originY); // top
      ctx.fillRect(originX, rect.y + rect.h, drawnW, (originY + drawnH) - (rect.y + rect.h)); // bottom
      ctx.fillRect(originX, rect.y, rect.x - originX, rect.h); // left
      ctx.fillRect(rect.x + rect.w, rect.y, (originX + drawnW) - (rect.x + rect.w), rect.h); // right

      const selectionColor = resolveCssVar('--selection', '#16bcdc');
      ctx.strokeStyle = selectionColor;
      ctx.lineWidth = 2;
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);

      ctx.fillStyle = selectionColor;
      for (const [hx, hy] of handlePositions(rect)) {
        ctx.fillRect(hx - HANDLE_SIZE / 2, hy - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
      }
    }
  }, [imgLoaded, rotation, cropMode, cropInsets, highlightHex]);

  const hitTestHandle = useCallback((px: number, py: number, rect: { x: number; y: number; w: number; h: number }): DragTarget | null => {
    const positions: Array<[number, number, DragTarget['kind'] extends 'handle' ? any : never]> = [] as any;
    const pts: Array<{ x: number; y: number; edge: Extract<DragTarget, { kind: 'handle' }>['edge'] }> = [
      { x: rect.x, y: rect.y, edge: 'nw' }, { x: rect.x + rect.w / 2, y: rect.y, edge: 'n' }, { x: rect.x + rect.w, y: rect.y, edge: 'ne' },
      { x: rect.x, y: rect.y + rect.h / 2, edge: 'w' }, { x: rect.x + rect.w, y: rect.y + rect.h / 2, edge: 'e' },
      { x: rect.x, y: rect.y + rect.h, edge: 'sw' }, { x: rect.x + rect.w / 2, y: rect.y + rect.h, edge: 's' }, { x: rect.x + rect.w, y: rect.y + rect.h, edge: 'se' },
    ];
    for (const p of pts) {
      if (Math.abs(px - p.x) <= HANDLE_SIZE && Math.abs(py - p.y) <= HANDLE_SIZE) return { kind: 'handle', edge: p.edge };
    }
    if (px >= rect.x && px <= rect.x + rect.w && py >= rect.y && py <= rect.y + rect.h) return { kind: 'move' };
    return null;
  }, []);

  const getCanvasPoint = (e: React.PointerEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const bounds = canvas.getBoundingClientRect();
    // Canvas backing store is a fixed PREVIEW_BOX square regardless of its
    // displayed CSS size, so map client coordinates through that ratio —
    // matters if this component is ever displayed scaled (e.g. via CSS)
    // rather than 1:1.
    return {
      x: ((e.clientX - bounds.left) / bounds.width) * PREVIEW_BOX,
      y: ((e.clientY - bounds.top) / bounds.height) * PREVIEW_BOX,
    };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!cropMode) return;
    const { x, y } = getCanvasPoint(e);
    const rect = insetsToRect(cropInsets, rotatedLayout.x, rotatedLayout.y, rotatedLayout.w, rotatedLayout.h);
    const target = hitTestHandle(x, y, rect);
    if (!target) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    dragRef.current = { target, startX: x, startY: y, startInsets: cropInsets };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current || !cropMode) return;
    const { x, y } = getCanvasPoint(e);
    const dx = x - dragRef.current.startX;
    const dy = y - dragRef.current.startY;
    const { x: ox, y: oy, w: ow, h: oh } = rotatedLayout;
    const startRect = insetsToRect(dragRef.current.startInsets, ox, oy, ow, oh);
    const next = applyDragToRect(startRect, dragRef.current.target, dx, dy, ox, oy, ow, oh);
    onCropInsetsChange(rectToInsets(next, ox, oy, ow, oh));
  };

  const endDrag = () => { dragRef.current = null; };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', background: '#f1f5f9', borderRadius: 8, padding: 8 }}>
      {!imgLoaded ? (
        <div style={{ width: PREVIEW_BOX, height: PREVIEW_BOX, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
          …
        </div>
      ) : (
        <canvas
          ref={canvasRef}
          width={PREVIEW_BOX}
          height={PREVIEW_BOX}
          style={{ width: PREVIEW_BOX, height: PREVIEW_BOX, touchAction: 'none', cursor: cropMode ? 'crosshair' : 'default', borderRadius: 6 }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        />
      )}
    </div>
  );
}

function insetsToRect(insets: CropInsets, ox: number, oy: number, ow: number, oh: number) {
  const x = ox + (insets.left / 100) * ow;
  const y = oy + (insets.top / 100) * oh;
  const w = ow - (insets.left + insets.right) / 100 * ow;
  const h = oh - (insets.top + insets.bottom) / 100 * oh;
  return { x, y, w, h };
}

function rectToInsets(rect: { x: number; y: number; w: number; h: number }, ox: number, oy: number, ow: number, oh: number): CropInsets {
  const left = ((rect.x - ox) / ow) * 100;
  const top = ((rect.y - oy) / oh) * 100;
  const right = ((ox + ow - (rect.x + rect.w)) / ow) * 100;
  const bottom = ((oy + oh - (rect.y + rect.h)) / oh) * 100;
  return {
    top: clamp(top, 0, 45), right: clamp(right, 0, 45),
    bottom: clamp(bottom, 0, 45), left: clamp(left, 0, 45),
  };
}

function clamp(v: number, min: number, max: number) { return Math.max(min, Math.min(max, v)); }

// Per-channel distance tolerance for "counts as the hovered swatch's color"
// — generous enough to survive the preview canvas's own downscale/
// anti-aliasing softening a swatch's exact hex at PREVIEW_BOX resolution
// (the quantized source image is flat-color, but resampling it into this
// small canvas blends edge pixels), tight enough that visually distinct
// palette colors don't bleed into each other's highlight.
const HIGHLIGHT_TOLERANCE = 40;
const HIGHLIGHT_DIM_ALPHA = 0.82; // fraction each non-matching pixel is darkened by

// Darkens every pixel within the drawn image's own bounds (ox,oy,ow,oh —
// NOT the full PREVIEW_BOX, which includes checkerboard/letterboxed margin
// that should stay untouched) that isn't within HIGHLIGHT_TOLERANCE of
// `hex`, leaving matching pixels at full brightness — a plain in-place
// getImageData/putImageData pass, run once per redraw rather than per
// pointer-move (the hover handler only flips React state, this effect's own
// dependency array is what re-triggers the draw).
function dimNonMatchingPixels(ctx: CanvasRenderingContext2D, ox: number, oy: number, ow: number, oh: number, hex: string) {
  const x = Math.max(0, Math.floor(ox)), y = Math.max(0, Math.floor(oy));
  const w = Math.min(PREVIEW_BOX - x, Math.ceil(ow));
  const h = Math.min(PREVIEW_BOX - y, Math.ceil(oh));
  if (w <= 0 || h <= 0) return;

  const h6 = hex.startsWith('#') ? hex : `#${hex}`;
  const tr = parseInt(h6.slice(1, 3), 16) || 0;
  const tg = parseInt(h6.slice(3, 5), 16) || 0;
  const tb = parseInt(h6.slice(5, 7), 16) || 0;

  // getImageData throws SecurityError on a canvas tainted by a cross-origin
  // draw that didn't actually serve CORS headers despite img.crossOrigin =
  // 'anonymous' being set (the <img> load itself still succeeds in that
  // case — only pixel readback is blocked) — fails soft (no highlight, but
  // the rest of the preview keeps working) rather than crashing the wizard.
  let imageData: ImageData;
  try {
    imageData = ctx.getImageData(x, y, w, h);
  } catch {
    return;
  }
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0) continue; // transparent pixel — nothing to dim/highlight
    const dr = data[i] - tr, dg = data[i + 1] - tg, db = data[i + 2] - tb;
    const matches = Math.sqrt(dr * dr + dg * dg + db * db) <= HIGHLIGHT_TOLERANCE;
    if (!matches) {
      data[i] *= 1 - HIGHLIGHT_DIM_ALPHA;
      data[i + 1] *= 1 - HIGHLIGHT_DIM_ALPHA;
      data[i + 2] *= 1 - HIGHLIGHT_DIM_ALPHA;
    }
  }
  ctx.putImageData(imageData, x, y);
}

function handlePositions(rect: { x: number; y: number; w: number; h: number }): Array<[number, number]> {
  return [
    [rect.x, rect.y], [rect.x + rect.w / 2, rect.y], [rect.x + rect.w, rect.y],
    [rect.x, rect.y + rect.h / 2], [rect.x + rect.w, rect.y + rect.h / 2],
    [rect.x, rect.y + rect.h], [rect.x + rect.w / 2, rect.y + rect.h], [rect.x + rect.w, rect.y + rect.h],
  ];
}

// Resizes/repositions `startRect` by (dx, dy) canvas-pixels according to what
// was grabbed, clamped so the rect never inverts or shrinks below
// MIN_CROP_PX, and never leaves the rotated image's own drawn bounds
// (ox,oy,ow,oh) — dragging a handle past the opposite edge/corner just stops
// at the minimum size rather than flipping the rectangle.
function applyDragToRect(
  startRect: { x: number; y: number; w: number; h: number },
  target: DragTarget,
  dx: number, dy: number,
  ox: number, oy: number, ow: number, oh: number,
): { x: number; y: number; w: number; h: number } {
  let { x, y, w, h } = startRect;

  if (target.kind === 'move') {
    x = clamp(startRect.x + dx, ox, ox + ow - w);
    y = clamp(startRect.y + dy, oy, oy + oh - h);
    return { x, y, w, h };
  }

  const { edge } = target;
  const hasN = edge.includes('n'), hasS = edge.includes('s'), hasE = edge.includes('e'), hasW = edge.includes('w');

  if (hasW) {
    const newX = clamp(startRect.x + dx, ox, startRect.x + startRect.w - MIN_CROP_PX);
    w = startRect.x + startRect.w - newX;
    x = newX;
  }
  if (hasE) {
    const newRight = clamp(startRect.x + startRect.w + dx, startRect.x + MIN_CROP_PX, ox + ow);
    w = newRight - x;
  }
  if (hasN) {
    const newY = clamp(startRect.y + dy, oy, startRect.y + startRect.h - MIN_CROP_PX);
    h = startRect.y + startRect.h - newY;
    y = newY;
  }
  if (hasS) {
    const newBottom = clamp(startRect.y + startRect.h + dy, startRect.y + MIN_CROP_PX, oy + oh);
    h = newBottom - y;
  }

  return { x, y, w, h };
}
